import type { OnboardingStepDeps } from '@/composables/useOnboarding';
import i18n from '@/i18n';
import { createActionStep, createInfoStep } from '@/onboarding/stepStyles';
import type { StepOptions } from 'shepherd.js';
import { nextTick } from 'vue';
import type { Composer } from 'vue-i18n';

const t = (key: string) => (i18n.global as unknown as Composer).t(key);

export function getRecipeDetailsSteps({ tour, waitForElement }: OnboardingStepDeps): StepOptions[] {
  return [
    createActionStep(
      {
        id: 'recipe-details',
        title: t('onboarding.chapters.recipeDetails.steps.recipe-details.title'),
        text: t('onboarding.chapters.recipeDetails.steps.recipe-details.text'),
        attachTo: { element: '.placeholder-image', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '.placeholder-image', event: 'click' },
        beforeShowPromise: async () => {
          // Nettoyer les images existantes au début du chapitre
          const dishesStore = (await import('@/stores/dishesStore')).useDishesStore();
          if (dishesStore.selectedDish) {
            // Réinitialiser toutes les propriétés d'image
            dishesStore.selectedDish.image_url_gastro = undefined;
            dishesStore.selectedDish.image_url_bistro = undefined;
            dishesStore.selectedDish.image_url_user = undefined;
            dishesStore.selectedDish.selected_image = 'none';

            // Mettre à jour aussi dans le tableau dishes si le plat y existe
            const dishIndex = dishesStore.dishes.findIndex((dish) => dish.id === dishesStore.selectedDish?.id);
            if (dishIndex !== -1) {
              dishesStore.dishes[dishIndex].image_url_gastro = undefined;
              dishesStore.dishes[dishIndex].image_url_bistro = undefined;
              dishesStore.dishes[dishIndex].image_url_user = undefined;
              dishesStore.dishes[dishIndex].selected_image = 'none';
            }
          }

          await nextTick();
          await waitForElement('.placeholder-image', 10000);
        },
      },
      {
        placement: 'top',
      },
    ),

    createActionStep(
      {
        id: 'image-generation',
        title: t('onboarding.chapters.recipeDetails.steps.image-generation.title'),
        text: t('onboarding.chapters.recipeDetails.steps.image-generation.text'),
        attachTo: { element: '.onboarding-placeholder-menu', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-menu-generate-image', event: 'click' },
        beforeShowPromise: async () => {
          // Nettoyer les images existantes si elles existent
          const dishesStore = (await import('@/stores/dishesStore')).useDishesStore();
          if (dishesStore.selectedDish) {
            // Réinitialiser toutes les propriétés d'image
            dishesStore.selectedDish.image_url_gastro = undefined;
            dishesStore.selectedDish.image_url_bistro = undefined;
            dishesStore.selectedDish.image_url_user = undefined;
            dishesStore.selectedDish.selected_image = 'none';

            // Mettre à jour aussi dans le tableau dishes si le plat y existe
            const dishIndex = dishesStore.dishes.findIndex((dish) => dish.id === dishesStore.selectedDish?.id);
            if (dishIndex !== -1) {
              dishesStore.dishes[dishIndex].image_url_gastro = undefined;
              dishesStore.dishes[dishIndex].image_url_bistro = undefined;
              dishesStore.dishes[dishIndex].image_url_user = undefined;
              dishesStore.dishes[dishIndex].selected_image = 'none';
            }
          }

          await nextTick();
          await waitForElement('.onboarding-placeholder-menu', 10000);
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    // // 11.1 Attente des images générées
    // createInfoStep({
    //   id: 'wait-generated-images',
    //   title: '⏳🖼️ Génération des images en cours',
    //   text: "Patientez pendant que l'IA génère des images pour votre recette.",
    //   attachTo: { element: '.onboarding-wait-generated-images', on: 'bottom' },
    //   canClickTarget: false,
    //   beforeShowPromise: async () => {
    //     try {
    //       await waitForElement('.onboarding-wait-generated-images', 15000);
    //       await nextTick();
    //     } catch (error) {
    //       console.error('[Onboarding] Error waiting for generated-image-option:', error);
    //     }
    //   },
    //   buttons: [],
    // }),
    // 11.2 -Choix de l'image générée
    createActionStep(
      {
        id: 'select-generated-image',
        title: t('onboarding.chapters.recipeDetails.steps.select-generated-image.title'),
        text: t('onboarding.chapters.recipeDetails.steps.select-generated-image.text'),
        attachTo: { element: '.onboarding-generated-images', on: 'bottom' },
        canClickTarget: true,
        beforeShowPromise: async () => {
          await waitForElement('.onboarding-generated-images', 10000);
          await nextTick();
        },
      },
      {
        placement: 'top',
      },
    ),

    createInfoStep({
      id: 'adjust-servings',
      title: t('onboarding.chapters.recipeDetails.steps.adjust-servings.title'),
      text: t('onboarding.chapters.recipeDetails.steps.adjust-servings.text'),
      attachTo: { element: '.onboarding-input-number-of-people', on: 'bottom' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        await nextTick();
        await waitForElement('.onboarding-input-number-of-people', 10000);
      },
      buttons: [
        {
          text: t('onboarding.common.next'),
          action: () => {
            tour.value?.next();
          },
        },
      ],
    }),
  ];
}
